import { Alert, Button, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material'
import type { ComponentType } from 'preact'
import { useEffect } from 'preact/hooks'
import { LocationProvider, lazy, Route, Router, useLocation } from 'preact-iso'
import { withHeader } from './AppHeader'
import { EmailRequired } from './auth/EmailRequired'
import { loginUrl } from './auth/next'
import { PasskeyOffer } from './auth/PasskeyOffer'
import { SessionProvider, useSession } from './auth/session'
import { OfflineNotice } from './OfflineNotice'
import { EventNew } from './pages/EventNew'
import { EventSpace } from './pages/EventSpace'
import { Home } from './pages/Home'
import { Login } from './pages/Login'
import { LoginVerify } from './pages/LoginVerify'
import { NotFound } from './pages/NotFound'
import { theme } from './theme'

/** `offerPasskey`: false for pages that offer it themselves. */
function requireLogin(Page: ComponentType, { offerPasskey = true } = {}) {
  return function Protected() {
    const { me, loadError, reload } = useSession()
    const { route, url } = useLocation()
    useEffect(() => {
      if (me === null) route(loginUrl(url), true)
    }, [me])
    if (me && !me.email) return <EmailRequired />
    if (me)
      return (
        <>
          {offerPasskey && (
            <PasskeyOffer sx={{ mt: 2, mx: { xs: 2, sm: 'auto' }, maxWidth: 'sm' }} />
          )}
          <Page />
        </>
      )
    if (loadError)
      return (
        <Alert
          severity="error"
          sx={{ m: 4 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Réessayer
            </Button>
          }
        >
          {loadError}
        </Alert>
      )
    return <CircularProgress sx={{ m: 4 }} />
  }
}

const ProtectedHome = withHeader(requireLogin(Home, { offerPasskey: false }))
const ProtectedEventNew = withHeader(requireLogin(EventNew))
// The volunteer's page and the organizer space offer the passkey inside their layout
const ProtectedEventSpace = requireLogin(EventSpace, { offerPasskey: false })
const ProtectedPrintPage = requireLogin(
  lazy(() => import('./pages/PrintPage').then((m) => m.PrintPage)),
  { offerPasskey: false },
)
const ProtectedAdmin = withHeader(
  requireLogin(lazy(() => import('./pages/Admin').then((m) => m.Admin))),
)
// The profile lists the passkeys, with a button to add one
const ProtectedProfile = withHeader(
  requireLogin(
    lazy(() => import('./pages/Profile').then((m) => m.Profile)),
    { offerPasskey: false },
  ),
)
const AttendancePage = withHeader(
  lazy(() => import('./pages/AttendancePage').then((m) => m.AttendancePage)),
)
const PrivacyPage = withHeader(lazy(() => import('./pages/PrivacyPage').then((m) => m.PrivacyPage)))
const JoinPage = withHeader(lazy(() => import('./pages/JoinPage').then((m) => m.JoinPage)))
const ProtectedJoinPreview = withHeader(
  requireLogin(
    lazy(() => import('./pages/JoinPage').then((m) => m.JoinPreview)),
    { offerPasskey: false },
  ),
)
const PageNotFound = withHeader(NotFound)

export function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <OfflineNotice />
      <SessionProvider>
        <LocationProvider>
          <Router>
            <Route path="/" component={ProtectedHome} />
            <Route path="/admin" component={ProtectedAdmin} />
            <Route path="/profile" component={ProtectedProfile} />
            <Route path="/events/new" component={ProtectedEventNew} />
            <Route path="/events/:slug/join" component={JoinPage} />
            <Route path="/events/:slug/join/preview" component={ProtectedJoinPreview} />
            <Route path="/events/:slug/print" component={ProtectedPrintPage} />
            {/* Same component on both: the space stays mounted from one of its pages to another */}
            <Route path="/events/:slug" component={ProtectedEventSpace} />
            <Route path="/events/:slug/*" component={ProtectedEventSpace} />
            <Route path="/attendance" component={AttendancePage} />
            <Route path="/login" component={Login} />
            <Route path="/privacy" component={PrivacyPage} />
            <Route path="/login/verify" component={LoginVerify} />
            <Route default component={PageNotFound} />
          </Router>
        </LocationProvider>
      </SessionProvider>
    </ThemeProvider>
  )
}
